import { getAssetPath } from './assetPath';

export type Environment =
  | 'local'
  | 'development'
  | 'staging'
  | 'qa'
  | 'production';

export const getEnvironment = (): Environment => {
  const env = import.meta.env.VITE_APP_ENV;

  if (!['Local', 'Development', 'Staging', 'QA', 'Production'].includes(env)) {
    throw new Error(`Invalid environment: ${env}`);
  }

  return env.toLowerCase() as Environment;
};

// Hardcoded file paths for data resources
export const DATA_FILES = {
  CASES_INDEX: 'cases-index.json',
  GAME_CONFIG: 'clue_hunter_config.json',
  CASE_VISIBILITY: 'cases-visibility.json',
  CASE_DATA: '{caseId}.json',
  DAILY_SCHEDULE: 'daily-schedule.json',
} as const;

interface EnvConfig {
  url: string;
  bucket: string | null;
  path: string;
}

export const ENV_CONFIG = {
  local: {
    url: `http://localhost:4443/download/storage/v1/b`,
    bucket: 'my-local-bucket_2',
    path: 'o/games/clue_hunter/data/json-data',
  },
  development: {
    url: 'https://media.dev.wolf.games',
    bucket: null,
    path: 'games/clue_hunter/data/json-data',
  },
  qa: {
    url: 'https://media.qa.wolf.games',
    bucket: null,
    path: 'games/clue_hunter/data/json-data',
  },
  staging: {
    url: 'https://media.staging.wolf.games',
    bucket: null,
    path: 'games/clue_hunter/data/json-data',
  },
  production: {
    url: 'https://media.wolf.games',
    bucket: null,
    path: 'games/clue_hunter/data/json-data',
  },
};

export const getEnvConfig = (): EnvConfig => {
  const env = getEnvironment();

  const config = ENV_CONFIG[env];

  return config;
};

/**
 * Builds a complete URL by combining the public storage path with a file path
 * and optionally replacing placeholders with provided parameters
 */
export const buildDataUrl = (
  filePath: string,
  params?: Record<string, string>,
): string => {
  const config = getEnvConfig();
  let path = filePath;

  // Replace placeholders like {caseId} with actual values
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`{${key}}`, value);
    });
  }

  // Ensure proper URL encoding for the path
  const encodedPath = path.split('/').map(encodeURIComponent).join('/');

  if (import.meta.env.VITE_IS_FROZEN) {
    return getAssetPath(`/assets/data/${path}`);
  }

  return `${config.url}${config.bucket ? `/${config.bucket}` : ''}/${config.path}/${encodedPath}`;
};
